// Rediseño · Bloque E — cómo se entra a «Nuevo producto» y cómo se sale, sin reestructurar el router.
//
// · La pantalla tiene ruta propia, `#nuevo-producto` (navConfig, ROUTES_OUTSIDE_MENU): un enlace, una
//   recarga o el Atrás la reconstruyen. No usa `#other-sales/<x>`, donde el segundo segmento es el id de
//   una venta (N8, FUTURE_UPGRADES #47).
// · «Tus productos» —a donde P-029 regresa— es una pestaña de Ventas Otros que no tenía enlace. Para
//   poder volver a ella se reconoce UN token de vista, `#other-sales/catalog`.
// · N8: antes, CUALQUIER segundo segmento se leía como el id de una venta, y `#other-sales/pos` abría
//   el historial con un detalle que fallaba. Los ids de venta son UUID (el esquema y el servidor lo
//   exigen), así que ahora solo un UUID es una venta: «Ver registro» (Solicitudes) y el desglose de
//   Cortes, que llevan uno, abren su venta como siempre. Lo que no es ni el token ni un id se ignora:
//   la página abre su vista de siempre.
// · Al salir se vuelve a donde estaba la persona. Si llegó navegando dentro de la app, con el Atrás del
//   historial (así el Atrás siguiente no la devuelve al asistente). Si cargó la ruta directa, al
//   catálogo, reemplazando la entrada.

export const NEW_PRODUCT_HASH = 'nuevo-producto';
export const CATALOG_HASH = 'other-sales/catalog';
const CATALOG_TOKEN = 'catalog';
/**
 * P-075 (D-1, decisión del dueño): «Vender» abre el punto de venta directo. El punto de venta NO se mueve
 * de ruta: sigue siendo la vista de venta de `#other-sales` (Ventas e ingresos), y esta es su dirección.
 * Mientras se vende, el token se queda en la URL (una recarga vuelve a vender y el menú resalta «Vender»).
 */
export const POS_HASH = 'other-sales/vender';
const POS_TOKEN = 'vender';

const SALE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Lo que pide el segundo segmento de `#other-sales/<x>`: el catálogo, el punto de venta, una venta (un UUID), o nada. */
export interface OtherSalesHashTarget { catalog: boolean; sell: boolean; saleId: string | undefined }

export function otherSalesHashTarget(hash: string): OtherSalesHashTarget {
  const [first, second] = hash.replace('#', '').split('/');
  if (first !== 'other-sales' || !second) return { catalog: false, sell: false, saleId: undefined };
  if (second === CATALOG_TOKEN) return { catalog: true, sell: false, saleId: undefined };
  if (second === POS_TOKEN) return { catalog: false, sell: true, saleId: undefined };
  return { catalog: false, sell: false, saleId: SALE_ID.test(second) ? second : undefined };
}

/**
 * Al abrir la página, el token del catálogo —o un segundo segmento que no es nada— se consume: la URL
 * vuelve a `#other-sales`, la de siempre mientras se ve el catálogo o la vista de siempre. Así una
 * recarga se comporta como hoy y la URL no dice algo que la pantalla no es. Un id de venta se queda (la
 * página lo quita al cerrar su detalle, como siempre), y el del punto de venta también (P-075).
 * Sin evento de cambio de hash: no es una navegación.
 */
export function consumeCatalogToken(): void {
  const second = window.location.hash.replace('#', '').split('/')[1];
  const target = otherSalesHashTarget(window.location.hash);
  if (second && !target.saleId && !target.sell) window.history.replaceState(window.history.state, '', '#other-sales');
}

/**
 * Desde el catálogo: la entrada actual pasa a ser `#other-sales/catalog` (sin navegar) y se abre el
 * asistente. Al volver con el Atrás, la página reabre el catálogo, no el punto de venta.
 */
export function openNewProductFromCatalog(): void {
  window.history.replaceState(window.history.state, '', `#${CATALOG_HASH}`);
  window.location.hash = NEW_PRODUCT_HASH;
}

const ENTRY_KEY = 'n360NewProduct';
export type NewProductEntry = 'app' | 'direct';

function readEntry(): NewProductEntry | undefined {
  const state: unknown = window.history.state;
  if (state && typeof state === 'object' && ENTRY_KEY in state) {
    const value = (state as Record<string, unknown>)[ENTRY_KEY];
    if (value === 'app' || value === 'direct') return value;
  }
  return undefined;
}

/**
 * Marca la entrada del asistente la primera vez que se abre: «app» si se llegó navegando, «direct» si se
 * cargó así. La marca viaja con la entrada del historial, así que sobrevive a una recarga y a ir y
 * volver con el Atrás y el Adelante.
 */
export function markNewProductEntry(arrivedInApp: boolean): NewProductEntry {
  const existing = readEntry();
  if (existing) return existing;
  const entry: NewProductEntry = arrivedInApp ? 'app' : 'direct';
  const state: unknown = window.history.state;
  window.history.replaceState({ ...(state && typeof state === 'object' ? state : {}), [ENTRY_KEY]: entry }, '');
  return entry;
}

/**
 * Salir del asistente: a donde estaba la persona, o al catálogo si cargó la ruta directa. Con la guarda
 * del Atrás del navegador encima (ver más abajo), se salta también: la entrada del asistente queda
 * detrás, como si la guarda no existiera.
 */
export function leaveNewProduct(): void {
  const guarded = onBrowserBackGuard();
  leaving = true;
  if (readEntry() === 'app') {
    if (guarded) window.history.go(-2);
    else window.history.back();
  } else if (guarded) {
    // Ruta directa: primero se quita la guarda y, ya en la entrada del asistente, se reemplaza por el
    // catálogo (reemplazar la guarda dejaría el asistente detrás del catálogo).
    const onPop = () => {
      window.removeEventListener('popstate', onPop);
      window.location.replace(`#${CATALOG_HASH}`);
    };
    window.addEventListener('popstate', onPop);
    window.history.back();
  } else {
    window.location.replace(`#${CATALOG_HASH}`);
  }
}

// ── FI-93 (P-074): el Atrás del NAVEGADOR también regresa una pregunta ─────────────────────────────────
//
// En la app (APK) el Atrás del teléfono lo atiende Capacitor y nunca toca el historial (`phoneBack.ts`).
// En la web abierta en un navegador, el Atrás es el del historial, y los pasos del asistente no eran
// entradas: se salía de «Nuevo producto» perdiendo lo escrito. Ahora, mientras la pantalla está abierta,
// hay UNA entrada más encima de la suya, con la misma dirección (sin evento de cambio de hash):
// la guarda. El Atrás del navegador la saca y el asistente hace lo mismo que «‹ Atrás»: un paso, con lo
// escrito y sin sacar el teclado (D-UI-G-WIZARD-BACK-NO-KEYBOARD); desde la primera pregunta, o ya creado,
// la salida de siempre (`leaveNewProduct`); mientras se crea, nada. Si se queda, la guarda vuelve a ponerse.
//
//   · Nunca hay más de una guarda: volver a dibujar la pantalla no toca el historial, y una guarda que ya
//     está (una recarga a medio asistente, o volver a él con el Atrás) se adopta en vez de duplicarse.
//   · Si el Atrás lleva a otra pantalla (el menú de historial del navegador salta varias entradas), no se
//     hace nada: el router ya se va de aquí.
//   · El Adelante no avanza preguntas: tras regresar, se está otra vez sobre la guarda y no queda nada delante.
//
// FI-128 (P-081-C1) · VOLVER A PONER LA GUARDA ES UN ADELANTE, NO UNA ENTRADA NUEVA. Chrome (la «intervención de
// manipulación del historial») marca para saltar TODAS las entradas de una página que crea una entrada sin que la
// persona haya tocado la pantalla desde el último Atrás, y su botón Atrás sale de la app. Volver a poner la guarda
// con `pushState` justo después de un Atrás era eso: medido en el Chrome del emulador, en el paso 3 el segundo Atrás
// salía de Negocio360. La guarda que el Atrás sacó sigue DELANTE: se vuelve a ella con `history.forward()`, que no
// crea nada (lo mismo que `posBrowserBack.ts`, D-P081-C1-GUARD-BY-FORWARD). `pushState` queda para la primera guarda.

const GUARD_KEY = 'n360NewProductGuard';
/** Si el Adelante propio no llega (no había guarda delante), se deja de esperarlo. */
const RESTORE_TIMEOUT_MS = 1000;
// Se está saliendo del asistente: los `popstate` que lleguen son de esa salida, no del Atrás.
let leaving = false;
// FI-128: la guarda quedó DELANTE (la sacó un Atrás), y un Adelante propio para volver a ella está en camino.
let guardAhead = false;
let restoring = false;
let restoreTimer: number | undefined;

/** ¿La entrada actual es la guarda del asistente? */
export function onBrowserBackGuard(): boolean {
  const state: unknown = window.history.state;
  return Boolean(state && typeof state === 'object' && (state as Record<string, unknown>)[GUARD_KEY] === true);
}

function armBrowserBackGuard(): void {
  if (leaving || restoring || onBrowserBackGuard()) return;
  if (guardAhead) {
    guardAhead = false;
    restoring = true;
    // Si no había nada delante, no llega ningún `popstate`: se deja de esperar y NO se crea una entrada.
    restoreTimer = window.setTimeout(() => { restoreTimer = undefined; restoring = false; }, RESTORE_TIMEOUT_MS);
    window.history.forward();
    return;
  }
  const state: unknown = window.history.state;
  // Lleva la marca de la entrada (`n360NewProduct`): leaveNewProduct la lee igual desde aquí.
  window.history.pushState({ ...(state && typeof state === 'object' ? state : {}), [GUARD_KEY]: true }, '');
}

/**
 * Mientras la pantalla de «Nuevo producto» está montada (también mientras cargan las tiendas): pone la
 * guarda y, con cada Atrás del navegador que la saca, llama a `stepBack` (lo mismo que el Atrás del
 * teléfono: un paso del asistente o, sin asistente, salir). Devuelve con qué dejar de escuchar; la guarda no
 * se quita al desmontar (quien navega ya dejó la entrada; si se vuelve a ella, se adopta).
 */
export function watchBrowserBack(stepBack: () => void): () => void {
  leaving = false;
  guardAhead = false;
  restoring = false;
  armBrowserBackGuard();
  const onPop = () => {
    if (leaving) return;
    if (restoring) {
      // De vuelta sobre la guarda con el Adelante propio: no es un Atrás de la persona.
      restoring = false;
      if (restoreTimer !== undefined) { window.clearTimeout(restoreTimer); restoreTimer = undefined; }
      return;
    }
    if (window.location.hash.replace('#', '').split('/')[0] !== NEW_PRODUCT_HASH) { guardAhead = false; return; }
    if (onBrowserBackGuard()) { guardAhead = false; return; }
    guardAhead = true;
    stepBack();
    armBrowserBackGuard();
  };
  window.addEventListener('popstate', onPop);
  return () => {
    window.removeEventListener('popstate', onPop);
    if (restoreTimer !== undefined) { window.clearTimeout(restoreTimer); restoreTimer = undefined; }
    restoring = false;
  };
}
